import Dexie, { type Table } from 'dexie';
import type {
  Incident, EPCR, VitalSet, Procedure, MedicationGiven,
  Narrative, FireReport, Apparatus, Addendum, Casualty,
  Attachment, Signature, EpcrAmendment,
  DeviceTransmission, DefibEvent, CPRQualityMetric,
  Code99Session, Code99Event,
  TripBreadcrumb,
} from '@/types';

// Extend types with offline sync metadata
export interface SyncMeta {
  _synced: boolean;
  _dirty: boolean;
  _deleted: boolean;
  _last_modified: string;
}

export type Offlineable<T> = T & SyncMeta;

// IndexedDB name kept as 'MoquinTrendDB' intentionally — Finding 84
// of the security audit flagged the historic name but renaming it
// requires a one-time per-user data migration that we cannot run
// safely mid-shift (pull old DB → push new DB → drop old; any failure
// strands unsynced records). Decision: leave the internal IndexedDB
// name; the user-visible brand is already EMSPCR.app everywhere. If a
// future need to rename arises, the migration plan is:
//   1. Open the legacy DB read-only.
//   2. Verify sync_queue.count() === 0 (all records synced to Supabase).
//   3. Open a new EMSPCRDB at version N (matching the latest
//      schema), copy every table row-by-row inside a single rw
//      transaction so a crash rolls back.
//   4. Compare row counts; only delete the legacy DB when they match.
//   5. Reload the page.
// Until then the class name is just an implementation detail.
class MoquinTrendDB extends Dexie {
  incidents!: Table<Offlineable<Incident>>;
  epcrs!: Table<Offlineable<EPCR>>;
  vital_sets!: Table<Offlineable<VitalSet>>;
  procedures!: Table<Offlineable<Procedure>>;
  medications_given!: Table<Offlineable<MedicationGiven>>;
  narratives!: Table<Offlineable<Narrative>>;
  fire_reports!: Table<Offlineable<FireReport>>;
  apparatus!: Table<Offlineable<Apparatus>>;
  addendums!: Table<Offlineable<Addendum>>;
  casualties!: Table<Offlineable<Casualty>>;
  attachments!: Table<Offlineable<Attachment>>;
  signatures!: Table<Offlineable<Signature>>;
  epcr_amendments!: Table<Offlineable<EpcrAmendment>>;
  device_transmissions!: Table<Offlineable<DeviceTransmission>>;
  defib_events!: Table<Offlineable<DefibEvent>>;
  cpr_quality_metrics!: Table<Offlineable<CPRQualityMetric>>;
  code99_sessions!: Table<Offlineable<Code99Session>>;
  code99_events!: Table<Offlineable<Code99Event>>;
  trip_breadcrumbs!: Table<Offlineable<TripBreadcrumb>>;
  sync_queue!: Table<SyncQueueItem>;

  constructor() {
    super('MoquinTrendDB');

    this.version(1).stores({
      incidents: 'id, agency_id, run_number, status, sync_status, dispatch_time, _synced, _dirty',
      epcrs: 'id, incident_id, agency_id, _synced, _dirty',
      vital_sets: 'id, epcr_id, timestamp, _synced, _dirty',
      procedures: 'id, epcr_id, timestamp, _synced, _dirty',
      medications_given: 'id, epcr_id, timestamp, _synced, _dirty',
      narratives: 'id, epcr_id, _synced, _dirty',
      fire_reports: 'id, incident_id, agency_id, _synced, _dirty',
      apparatus: 'id, incident_id, _synced, _dirty',
      addendums: 'id, fire_report_id, _synced, _dirty',
      casualties: 'id, fire_report_id, _synced, _dirty',
      sync_queue: '++id, table_name, record_id, operation, created_at, [table_name+record_id]',
    });

    // v2: added attachments + signatures tables
    this.version(2).stores({
      attachments: 'id, epcr_id, incident_id, fire_report_id, agency_id, category, _synced, _dirty',
      signatures: 'id, epcr_id, fire_report_id, agency_id, signer_role, _synced, _dirty',
    });

    // v3: epcr amendments (post-lock corrections)
    this.version(3).stores({
      epcr_amendments: 'id, epcr_id, agency_id, authored_at, _synced, _dirty',
    });

    // v4: device transmissions + defib events + cpr quality
    this.version(4).stores({
      device_transmissions: 'id, agency_id, epcr_id, incident_id, device_vendor, transmission_received_at, _synced, _dirty',
      defib_events: 'id, epcr_id, event_time, _synced, _dirty',
      cpr_quality_metrics: 'id, epcr_id, window_start, _synced, _dirty',
    });

    // v5: Code 99 resuscitation sessions + events
    this.version(5).stores({
      code99_sessions: 'id, epcr_id, agency_id, incident_id, activated_at, closed_at, disposition, _synced, _dirty',
      code99_events:   'id, session_id, epcr_id, agency_id, event_type, event_time, _synced, _dirty',
    });

    // v6: GPS breadcrumbs for billable mileage
    this.version(6).stores({
      trip_breadcrumbs: 'id, epcr_id, incident_id, agency_id, recorded_at, phase, _synced, _dirty',
    });

    // v7: sync_queue gets dead_lettered + next_attempt_at indexes so
    // the rewritten drain loop can filter cheaply (Sprint-3 sync
    // rewrite). The `[dead_lettered+next_attempt_at]` compound index
    // lets us pick the next-due-non-dead row without scanning.
    // Dexie schema upgrades are additive — existing rows survive.
    this.version(7).stores({
      sync_queue: '++id, table_name, record_id, operation, created_at, dead_lettered, next_attempt_at, [table_name+record_id], [dead_lettered+next_attempt_at]',
    });
  }
}

export interface SyncQueueItem {
  id?: number;
  table_name: string;
  record_id: string;
  operation: 'insert' | 'update' | 'delete';
  data: Record<string, unknown>;
  created_at: string;
  attempts: number;
  last_error: string | null;

  // Sprint-3 fields (Findings 34, 35, 39, 62).
  /**
   * User id at enqueue time. Sync refuses to flush a queue item if
   * the current session's user does not match this — closes the
   * "queued POST replays under the next medic's credentials after
   * shift change" attribution bug (Finding 34).
   */
  created_by_user_id?: string | null;
  /**
   * Earliest wall-clock time we'll re-attempt. Exponential backoff
   * uses this to space retries after 5xx / network failures.
   */
  next_attempt_at?: string | null;
  /**
   * True once the item has exhausted retries (or hit a poison-class
   * error). Excluded from normal drains; surfaces in the admin
   * "Failed syncs" view for manual review.
   */
  dead_lettered?: boolean;
  dead_lettered_at?: string | null;
  /**
   * Classification of the last failure. Drives UI copy in the
   * failed-syncs banner ('auth' vs 'conflict' vs 'server' etc.).
   */
  failure_class?: 'auth' | 'forbidden' | 'conflict' | 'validation' | 'server' | 'network' | 'quota' | 'unknown' | null;
}

export const db = new MoquinTrendDB();

// Helper to create a record with sync metadata
export function withSyncMeta<T>(record: T, synced = false): Offlineable<T> {
  return {
    ...record,
    _synced: synced,
    _dirty: !synced,
    _deleted: false,
    _last_modified: new Date().toISOString(),
  };
}
